import { env } from '@/lib/env';

/**
 * Helpers para URLs de assets Cloudinary de NQORO.
 *
 * Los videos hero viven en `nqoro/hero/*` y se sirven con transformaciones
 * on-the-fly: `f_auto,q_auto,w_N,c_limit` — Cloudinary elige mp4/webm según
 * Accept header, ajusta quality y hace cap al ancho.
 *
 * Para posters (thumbnails) usa `so_0` = seek offset 0s → primer frame.
 */

const CLOUD = env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;

export interface VideoSource {
  mp4: string;
  poster: string;
  width: number;
  height: number;
}

/**
 * Video URL sin transformaciones. Importante: Cloudinary solo responde
 * `Accept-Ranges: bytes` para el asset original. Cualquier transformación
 * custom (w_N, f_auto, q_auto sobre videos) devuelve `Accept-Ranges: none`
 * + `Cache-Control: no-cache`, lo cual rompe el `<video>` de HTML5 porque
 * Chrome/Safari necesitan byte-range para streaming. Servimos el original
 * h264 comprimido en origen; Cloudflare CDN cachea public max-age=30d.
 */
function videoUrl(publicId: string): string {
  if (!CLOUD) return '';
  return `https://res.cloudinary.com/${CLOUD}/video/upload/${publicId}.mp4`;
}

/**
 * Poster JPG del frame 0. Los posters sí soportan transformaciones porque
 * son imágenes estáticas, no streaming media.
 */
function posterUrl(publicId: string, width: number): string {
  if (!CLOUD) return '';
  return `https://res.cloudinary.com/${CLOUD}/video/upload/f_auto,q_auto,so_0,w_${width},c_limit/${publicId}.jpg`;
}

/**
 * Hero video del manifiesto. Dos orientaciones:
 *   - desktop: 640x480 landscape h264 (~16MB, 108s)
 *   - mobile: 480x848 portrait h264 (~19MB, 108s)
 */
export const HERO_VIDEO: { desktop: VideoSource; mobile: VideoSource } = {
  desktop: {
    mp4: videoUrl('nqoro/hero/hero-desktop'),
    poster: posterUrl('nqoro/hero/hero-desktop', 1280),
    width: 640,
    height: 480,
  },
  mobile: {
    mp4: videoUrl('nqoro/hero/hero-mobile'),
    poster: posterUrl('nqoro/hero/hero-mobile', 720),
    width: 480,
    height: 848,
  },
};

/**
 * Novela hero — propuesta `/novela` (segundo home).
 * Video vertical 576x1024 (~2MB) sube/loop sin sonido. Naye compara contra
 * el HeroCarousel de `/` antes de decidir cuál se promueve.
 */
export const NOVELA_VIDEO: VideoSource = {
  mp4: videoUrl('nqoro/novela/hero-loop'),
  poster: posterUrl('nqoro/novela/hero-loop', 1280),
  width: 576,
  height: 1024,
};

/**
 * Editoriales del catálogo Φ Código que acompañan `/novela`.
 * Subidos con `scripts/upload-novela-assets.mjs`.
 */
export const NOVELA_EDITORIALS = {
  playa: `https://res.cloudinary.com/${CLOUD}/image/upload/f_auto,q_auto,w_1600,c_limit/nqoro/novela/editorial-playa.jpg`,
  candle: `https://res.cloudinary.com/${CLOUD}/image/upload/f_auto,q_auto,w_900,c_limit/nqoro/novela/editorial-candle.png`,
  deck: `https://res.cloudinary.com/${CLOUD}/image/upload/f_auto,q_auto,w_900,c_limit/nqoro/novela/editorial-deck.png`,
  corsega: `https://res.cloudinary.com/${CLOUD}/image/upload/f_auto,q_auto,w_900,c_limit/nqoro/novela/nouveau-corsega.png`,
} as const;

/**
 * Productos reales del catálogo Φ Código de Naye, vivos en Cloudinary.
 * Subidos vía `scripts/upload-studio-hd.mjs`. Naye nombró cada pieza con
 * topónimos del Mediterráneo (Nouvēau: islas griegas + Adriático).
 */
function imgUrl(publicId: string, w = 900): string {
  return `https://res.cloudinary.com/${CLOUD}/image/upload/f_auto,q_auto,w_${w},c_limit/${publicId}`;
}

export const NOVELA_PRODUCTS = [
  {
    slug: 'corsega',
    name: 'CORSEGA',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/corsega'),
  },
  {
    slug: 'milos',
    name: 'MILOS',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/milos'),
  },
  {
    slug: 'creta',
    name: 'CRETA',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/creta'),
  },
  {
    slug: 'rodas',
    name: 'RODAS',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/rodas'),
  },
  { slug: 'ios', name: 'IOS', collection: 'Nouvēau', src: imgUrl('nqoro/collections/nouveau/ios') },
  {
    slug: 'oland',
    name: 'ÖLAND',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/oland'),
  },
  {
    slug: 'oshima',
    name: 'ŌSHIMA',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/oshima'),
  },
  {
    slug: 'chipre',
    name: 'CHIPRE',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/chipre'),
  },
  {
    slug: 'islandia',
    name: 'ISLANDIA',
    collection: 'Nouvēau',
    src: imgUrl('nqoro/collections/nouveau/islandia'),
  },
  {
    slug: 'vela',
    name: 'VELA',
    collection: 'Euphemē',
    src: imgUrl('nqoro/collections/eupheme/vela'),
  },
] as const;
